import { useEffect, useState } from "react";
import { FileText, CheckCircle2, DollarSign, MapPin } from "lucide-react";
import api from "../lib/api";

/**
 * Partner Portal home tile — 4 mini-cards mirroring the staff MyOfficeTile.
 *
 * - program_applications_this_month : new program applications in the current calendar month
 * - weekend_bookings_this_month     : weekend bookings with a tour_date in the current calendar month
 * - booked                          : approved applications + staff-attributed bookings
 * - pending_pricing                 : applications the auto-pricer couldn't match
 *
 * Each card links to the relevant portal tab via `onNavigate(tab)`.
 */
export default function PartnerHomeTile({ onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get("/partner-portal/home-summary")
      .then(({ data }) => { if (mounted) setData(data); })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  const items = [
    {
      key: "program_applications_this_month",
      label: "Program applications this month",
      icon: FileText,
      tab: "my-applications",
      tone: "text-sky-700 bg-sky-50 border-sky-200",
      testid: "phome-program-applications",
    },
    {
      key: "weekend_bookings_this_month",
      label: "Weekend bookings this month",
      icon: MapPin,
      tab: "weekend",
      tone: "text-violet-700 bg-violet-50 border-violet-200",
      testid: "phome-weekend-bookings",
    },
    {
      key: "booked",
      label: "Booked participants",
      icon: CheckCircle2,
      tab: "my-applications",
      tone: "text-emerald-700 bg-emerald-50 border-emerald-200",
      testid: "phome-booked",
    },
    {
      key: "pending_pricing",
      label: "Pending pricing",
      icon: DollarSign,
      tab: "my-applications",
      tone: "text-amber-700 bg-amber-50 border-amber-200",
      testid: "phome-pricing",
    },
  ];

  return (
    <div className="aii-card p-5" data-testid="phome-tile">
      <div className="text-[10px] uppercase tracking-widest text-brand font-bold flex items-center gap-1 mb-1">
        <FileText size={11} /> Your account at a glance
      </div>
      <div className="text-xs text-[color:var(--aii-muted)] mb-3">
        Live counts across your applications and content. Click a tile to jump straight in.
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {items.map(({ key, label, icon: Icon, tab, tone, testid }) => (
          <button
            type="button"
            key={key}
            data-testid={testid}
            onClick={() => onNavigate?.(tab)}
            className={`text-left border rounded-lg p-3 transition hover:shadow-sm ${tone}`}
          >
            <div className="flex items-center justify-between">
              <Icon size={14} />
              <span className="text-[10px] uppercase tracking-widest opacity-70">Live</span>
            </div>
            <div className="text-2xl font-display font-semibold mt-1.5">
              {loading ? "…" : (data?.[key] ?? 0)}
            </div>
            <div className="text-xs mt-0.5 opacity-90">{label}</div>
          </button>
        ))}
      </div>
    </div>
  );
}
